import { ReactComponent as CloseButton } from "assets/svg/outline-cross.svg";
import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import ConfirmModal from "components/confirm-modal";
import IconButton from "components/icon-button";
import useToggleModal from "hooks/useToggleModal";
import messages from "messages";
import { useCallback, useEffect, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  closeSidesheet,
  selectNewSessionDefaultStartTime,
  selectNewSessionHasInitialTime,
} from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import {
  AddNewSessionCancelHandler,
  useCalendarSidebarContext,
} from "../../context/CalendarSidebarContext";
import styles from "./AddToCalendar.module.scss";
import AddOrEditBlockInterval from "./components/add-or-edit-block-interval/AddOrEditBlockInterval";
import AddOrEditSession from "./components/add-or-edit-session/AddOrEditSession";
import { getNextMins } from "./utils/getNextMins";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import getClasses from "utils/getClasses";

const AMPLITUDE_COMPONENT_NAME = "add-new-session-modal";

const SESSION_TAB_KEY = "session";
const BLOCK_TAB_KEY = "block";

const AddToCalendar = () => {
  const dispatch = useDispatch();
  const { isPhonePortrait } = usePhonePortraitContext();

  const hasInitialTime = useSelector(selectNewSessionHasInitialTime);
  const defaultStartTime = useSelector(selectNewSessionDefaultStartTime);

  const minIntervalStart = new Date();
  const nextMin = getNextMins(minIntervalStart.getMinutes());
  // adjust minIntervalStarts date to time interval
  if (nextMin === 0) {
    minIntervalStart.setUTCHours(minIntervalStart.getUTCHours() + 1);
  }
  minIntervalStart.setMinutes(nextMin);
  minIntervalStart.setSeconds(0);
  minIntervalStart.setMilliseconds(0);

  const initialIntervalStart = defaultStartTime || +minIntervalStart;

  const [isDirty, setIsDirty] = useState(false);

  const {
    show: showConfirm,
    open: openConfirm,
    close: closeConfirm,
  } = useToggleModal(false);

  const close = useCallback(() => {
    dispatch(closeSidesheet());
  }, [dispatch]);

  const [handleCancelled, setHandleCancelled] =
    useState<AddNewSessionCancelHandler>();
  const { setRequestCancelAddNewSession } = useCalendarSidebarContext();
  useEffect(() => {
    const requestCancelAddNewSession = (
      onCancelled: AddNewSessionCancelHandler,
    ) => {
      if (isDirty) {
        setHandleCancelled(() => onCancelled);
        openConfirm();
      } else {
        close();
        if (onCancelled) {
          onCancelled();
        }
      }
    };
    setRequestCancelAddNewSession(() => requestCancelAddNewSession);
    return () => setRequestCancelAddNewSession(null);
  }, [close, isDirty, openConfirm, setRequestCancelAddNewSession]);

  const [currentTab, setCurrentTab] = useState(SESSION_TAB_KEY);

  const [
    showNewSessionTypeWarningOnCancel,
    setShowNewSessionTypeWarningOnCancel,
  ] = useState(false);
  return (
    <div
      className={getClasses(
        styles["add-to-calendar"],
        isPhonePortrait && styles["add-to-calendar--phone"],
      )}
    >
      <div className={styles["header"]}>
        <OutlinePlus className={`mr-1 ${styles["header__icon"]}`} />
        <div className={styles["header__title"]}>Add to Calendar</div>
        <IconButton
          onClick={() => {
            track(
              PRO_CALENDAR_ANALYTICS_PAGE_NAME,
              [AMPLITUDE_COMPONENT_NAME, "close-button"],
              "click",
            );
            isDirty ? openConfirm() : close();
          }}
        >
          <CloseButton />
        </IconButton>
      </div>

      <Tabs
        activeKey={currentTab}
        onSelect={(key) => setCurrentTab(key ?? SESSION_TAB_KEY)}
        className={styles["tabs"]}
      >
        <Tab eventKey={SESSION_TAB_KEY} title="Session">
          <AddOrEditSession
            initialIntervalStart={initialIntervalStart}
            hasInitialTime={hasInitialTime}
            setIsDirty={setIsDirty}
            setShowNewSessionTypeWarningOnCancel={
              setShowNewSessionTypeWarningOnCancel
            }
            isActive={currentTab === SESSION_TAB_KEY}
          />
        </Tab>
        <Tab
          eventKey={BLOCK_TAB_KEY}
          title="Block"
          disabled={defaultStartTime < Date.now()}
        >
          <AddOrEditBlockInterval
            initialIntervalStart={initialIntervalStart}
            setIsDirty={setIsDirty}
            isActive={currentTab === BLOCK_TAB_KEY}
          />
        </Tab>
      </Tabs>

      <ConfirmModal
        show={showConfirm}
        onConfirm={() => {
          closeConfirm();
          close();
          if (handleCancelled) {
            handleCancelled();
          }
        }}
        onCancel={closeConfirm}
        labelTitle={"Discard unsaved changes?"}
        labelCancel={messages.cancel}
        labelConfirm={messages.discard}
      >
        {showNewSessionTypeWarningOnCancel
          ? messages.areYouSureYourNewSessionType
          : null}
      </ConfirmModal>
    </div>
  );
};

export default AddToCalendar;
